import { z } from 'zod';
import { ActionTypeSchema, AggregationTypeSchema, CostCategorySchema, createPaginatedResponseSchema, EntityStatusSchema, IdParamsSchema, MetadataSchema, PaginationQuerySchema, } from '../common/index.js';
export const AgentSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    name: z.string().min(1).max(100),
    description: z.string().optional(),
    external_id: z.string().optional(),
    status: EntityStatusSchema,
    tags: z.array(z.string()).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const AgentActionSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    agent_id: z.string(),
    action_name: z.string().min(1).max(100),
    action_type: ActionTypeSchema,
    description: z.string().optional(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const AgentActionValueSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    agent_id: z.string(),
    agent_action_id: z.string(),
    fte_equivalent: z.number().optional(),
    revenue_per_unit: z.number().optional(),
    default_price_cents: z.number().int().optional(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const AgentActionBillableSignalSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    agent_action_id: z.string(),
    billable_signal_id: z.string(),
    is_primary: z.boolean().default(true),
    created_at: z.string().datetime(),
});
export const AgentActionCostSignalSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    agent_action_id: z.string(),
    cost_signal_id: z.string(),
    created_at: z.string().datetime(),
});
export const CreateAgentSchema = z.object({
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    external_id: z.string().max(100).optional(),
    status: EntityStatusSchema.default('draft'),
    tags: z.array(z.string()).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    metadata: MetadataSchema.optional(),
});
export const UpdateAgentSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(500).optional().nullable(),
    external_id: z.string().max(100).optional().nullable(),
    status: EntityStatusSchema.optional(),
    tags: z.array(z.string()).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    metadata: MetadataSchema.optional(),
});
export const CreateAgentActionSchema = z.object({
    action_name: z.string().min(1).max(100),
    action_type: ActionTypeSchema,
    description: z.string().max(500).optional(),
    billable_signal: z
        .object({
        signal_name: z.string().min(1).max(100),
        signal_code: z.string().min(1).max(100),
        aggregation_type: AggregationTypeSchema,
        unit_label: z.string().max(50).optional().default('units'),
        value_field: z.string().optional(),
        description: z.string().max(500).optional(),
    })
        .refine(data => {
        if (data.aggregation_type !== 'count' && !data.value_field) {
            return false;
        }
        return true;
    }, {
        message: "value_field is required when aggregation_type is not 'count'",
        path: ['value_field'],
    }),
    cost_signal_ids: z.array(z.string()).optional().default([]),
    value: z
        .object({
        fte_equivalent: z.number().optional(),
        revenue_per_unit: z.number().optional(),
        default_price_cents: z.number().int().optional(),
    })
        .optional(),
});
export const UpdateAgentActionSchema = z.object({
    action_name: z.string().min(1).max(100).optional(),
    action_type: ActionTypeSchema.optional(),
    description: z.string().max(500).optional().nullable(),
    billable_signal: z
        .object({
        signal_name: z.string().min(1).max(100).optional(),
        signal_code: z.string().min(1).max(100).optional(),
        aggregation_type: AggregationTypeSchema.optional(),
        unit_label: z.string().max(50).optional(),
        value_field: z.string().optional().nullable(),
        description: z.string().max(500).optional().nullable(),
    })
        .refine(data => {
        if (data.aggregation_type &&
            data.aggregation_type !== 'count' &&
            !data.value_field) {
            return false;
        }
        return true;
    }, {
        message: "value_field is required when aggregation_type is not 'count'",
        path: ['value_field'],
    })
        .optional(),
    cost_signal_ids: z.array(z.string()).optional(),
    add_cost_signal_ids: z.array(z.string()).optional(),
    remove_cost_signal_ids: z.array(z.string()).optional(),
    value: z
        .object({
        fte_equivalent: z.number().optional().nullable(),
        revenue_per_unit: z.number().optional().nullable(),
        default_price_cents: z.number().int().optional().nullable(),
    })
        .optional(),
});
export const AgentActionResponseSchema = AgentActionSchema.extend({
    billable_signal: z
        .object({
        signal_id: z.string(),
        signal_name: z.string(),
        signal_code: z.string(),
        aggregation_type: AggregationTypeSchema,
        unit_label: z.string(),
        value_field: z.string().optional(),
    })
        .optional(),
    cost_signals: z
        .array(z.object({
        signal_id: z.string(),
        signal_name: z.string(),
        signal_code: z.string(),
        unit_label: z.string(),
        description: z.string().nullable().optional(),
        cost_config: z
            .object({
            fixed_cost_cents: z.number().int().nullable().optional(),
            cost_category: CostCategorySchema.nullable().optional(),
        })
            .optional(),
    }))
        .optional(),
    value: z
        .object({
        fte_equivalent: z.number().nullable().optional(),
        revenue_per_unit: z.number().nullable().optional(),
        default_price_cents: z.number().int().nullable().optional(),
    })
        .optional(),
});
export const AgentResponseSchema = AgentSchema.extend({
    actions: z.array(AgentActionResponseSchema).optional(),
    billableSignalsCount: z.number().optional(),
    costSignalsCount: z.number().optional(),
    totalSignalsCount: z.number().optional(),
    taskSignalsCount: z.number().optional(),
    workflowSignalsCount: z.number().optional(),
    outcomeSignalsCount: z.number().optional(),
});
export const AgentQuerySchema = PaginationQuerySchema.extend({
    status: EntityStatusSchema.optional(),
    search: z.string().optional(),
    has_external_id: z.boolean().optional(),
});
export const AgentActionQuerySchema = PaginationQuerySchema.extend({
    agent_id: z.string().optional(),
    action_type: ActionTypeSchema.optional(),
    search: z.string().optional(),
});
export const PaginatedAgentsResponseSchema = createPaginatedResponseSchema(AgentResponseSchema);
export const PaginatedAgentActionsResponseSchema = createPaginatedResponseSchema(AgentActionResponseSchema);
export const AgentParamsSchema = IdParamsSchema;
export const AgentIdParamsSchema = z.object({
    agentId: z.string(),
});
export const AgentActionParamsSchema = z.object({
    agentId: z.string(),
    actionId: z.string(),
});
export const CreateAgentWithActionsSchema = z.object({
    agent: CreateAgentSchema,
    actions: z.array(CreateAgentActionSchema).min(1),
});
//# sourceMappingURL=index.js.map